import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import '@cloudscape-design/global-styles/index.css';

// Error boundary wrapper
class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean; error?: Error }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('React Error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ padding: '20px', fontFamily: 'sans-serif' }}>
          <h1>Error Loading Application</h1>
          <p>{this.state.error?.message || 'Unknown error'}</p>
          <pre style={{ background: '#f0f0f0', padding: '10px', marginTop: '10px' }}>
            {this.state.error?.stack}
          </pre>
        </div>
      );
    }

    return this.props.children;
  }
}

// Debug: Log environment variables
console.log('🔍 Environment variables check:');
console.log('  VITE_USER_POOL_ID:', import.meta.env.VITE_USER_POOL_ID || '❌ Missing');
console.log('  VITE_USER_POOL_CLIENT_ID:', import.meta.env.VITE_USER_POOL_CLIENT_ID || '❌ Missing');
console.log('  VITE_AGENT_RUNTIME_ARN:', import.meta.env.VITE_AGENT_RUNTIME_ARN || '❌ Missing');
console.log('  VITE_REGION:', import.meta.env.VITE_REGION || '❌ Missing');
console.log('  VITE_AGENT_BASE_URL:', import.meta.env.VITE_AGENT_BASE_URL || '❌ Missing (will use production AWS URL)');
console.log('  All env vars:', import.meta.env);

try {
  const rootElement = document.getElementById('root');
  if (!rootElement) {
    throw new Error('Root element not found');
  }

  console.log('✅ Root element found, rendering app...');
  
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </React.StrictMode>
  );
  
  console.log('✅ App rendered successfully');
} catch (error) {
  console.error('❌ Failed to render app:', error);
  document.body.innerHTML = `
    <div style="padding: 20px; font-family: sans-serif;">
      <h1>Failed to Initialize Application</h1>
      <p>${error instanceof Error ? error.message : 'Unknown error'}</p>
      <pre style="background: #f0f0f0; padding: 10px; margin-top: 10px;">
        ${error instanceof Error ? error.stack : String(error)}
      </pre>
    </div>
  `;
}
